import { Box, Typography } from '@mui/material';

import { COLORS } from '../../../IndividualPlan/tokens';

export type GradeBarColumn = {
  label: string;
  value: number | null;
  muted?: boolean;
};

const MAX_HEIGHT = 150;

export function GradeBars({
  title,
  columns,
  caption,
  footnote,
}: {
  title: string;
  columns: GradeBarColumn[];
  caption?: string;
  footnote?: string;
}) {
  const max = Math.max(1, ...columns.map((c) => c.value ?? 0));
  return (
    <Box
      data-testid="grade-bars"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
        p: 2,
        bgcolor: COLORS.bg,
        borderRadius: '8px',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'baseline',
          gap: 2,
          flexWrap: 'wrap',
        }}
      >
        <Typography component="p" sx={{ m: 0, fontSize: 16, fontWeight: 700, color: COLORS.ink }}>
          {title}
        </Typography>
        {caption && (
          <Typography
            component="p"
            sx={{ m: 0, fontSize: 12, fontWeight: 500, color: COLORS.muted }}
          >
            {caption}
          </Typography>
        )}
      </Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-end',
          gap: 1.5,
          height: MAX_HEIGHT + 24,
        }}
      >
        {columns.map((c) => (
          <Box
            key={c.label}
            data-testid="grade-bar"
            sx={{
              flex: '1 1 0',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 0.5,
            }}
          >
            <Typography
              component="span"
              sx={{ fontSize: 12, fontWeight: 600, color: COLORS.muted }}
            >
              {c.value == null ? 'n/a' : String(c.value)}
            </Typography>
            <Box
              sx={{
                width: '100%',
                maxWidth: 56,
                height: Math.round((MAX_HEIGHT * (c.value ?? 0)) / max),
                bgcolor:
                  c.value == null ? COLORS.hairlineSoft : c.muted ? COLORS.faint : COLORS.navy,
                borderRadius: '4px 4px 0 0',
                minHeight: c.value ? 2 : 0,
              }}
            />
          </Box>
        ))}
      </Box>
      <Box sx={{ display: 'flex', gap: 1.5 }}>
        {columns.map((c) => (
          <Typography
            key={c.label}
            component="span"
            sx={{
              flex: '1 1 0',
              textAlign: 'center',
              fontSize: 12,
              fontWeight: 600,
              color: c.value == null ? COLORS.faint : COLORS.muted,
            }}
          >
            {c.label}
          </Typography>
        ))}
      </Box>
      {footnote && (
        <Typography component="p" sx={{ m: 0, fontSize: 12, fontWeight: 500, color: COLORS.muted }}>
          {footnote}
        </Typography>
      )}
    </Box>
  );
}
